/**
 * Car physics — THE core of the game.
 *
 * `stepCar` is a pure function of (state, input, config, dt): no hidden state, no
 * randomness, no time queries. This is what makes client prediction + server
 * reconciliation trivial (replaying inputs = calling the function again) and what the
 * determinism unit tests lock down.
 *
 * Model: arcade forward/lateral decomposition in the car frame.
 * Per tick: steering (speed-dependent authority, inverted in reverse) → decompose
 * velocity in the new car frame → thrust/brake/rolling friction on the forward
 * component, exponential absorption on the lateral component (grip, with a drift
 * threshold + blend band) → recompose → integrate position.
 *
 * maxSpeed is a limiter, not a wall: held full throttle at the cap keeps
 * squeezing out speed — the overrev — asymptotically toward
 * maxSpeed*(1+OVERREV_HEADROOM). Lift, and it bleeds back below the cap.
 */

import { wrapAngle } from './math';

export const TICK_RATE = 60;
export const DT = 1 / TICK_RATE;

export interface CarState {
  x: number;
  y: number;
  /** Heading in radians. 0 = +x, increases clockwise in a y-down world. */
  angle: number;
  vx: number;
  vy: number;
}

/**
 * Driver inputs, normalized. Keyboard produces 0/1 values; analog devices
 * (Gamepad API, planned) will plug in without any protocol change.
 */
export interface CarInput {
  /** 0..1 — forward thrust. */
  throttle: number;
  /** 0..1 — brake, then reverse once stopped. */
  brake: number;
  /** -1..1 — negative steers left (counter-clockwise on screen), positive right. */
  steer: number;
}

export const NEUTRAL_INPUT: CarInput = { throttle: 0, brake: 0, steer: 0 };

export interface PhysicsConfig {
  /** px/s — forward speed limiter; full throttle overrevs a little past it. */
  maxSpeed: number;
  /** px/s² — forward thrust. */
  accel: number;
  /** px/s² — braking deceleration while moving forward. */
  brakeDecel: number;
  /** px/s² — reverse thrust once stopped. */
  reverseAccel: number;
  /** px/s — cap on reverse speed. */
  reverseMaxSpeed: number;
  /** rad/s — maximum steering rate. */
  turnRate: number;
  /** px/s — speed at which full steering authority is reached (0 at standstill). */
  turnInSpeed: number;
  /** 0..1 — fraction of steering authority lost as speed approaches maxSpeed. */
  highSpeedTurnDrop: number;
  /** 1/s — exponential absorption of lateral velocity (the grip). */
  lateralGrip: number;
  /** px/s — lateral speed above which grip starts fading toward driftGrip. */
  driftThreshold: number;
  /** px/s — width of the transition band between full grip and drift grip. */
  driftBlendBand: number;
  /** 1/s — reduced grip while sliding (small controlled drifts). */
  driftGrip: number;
  /** 1/s — exponential decay of forward speed when coasting. */
  rollingFriction: number;
  /** 0..1 — normal velocity bounce when hitting a wall. */
  wallRestitution: number;
  /** 0..1 — tangential speed loss factor scaled by impact angle. */
  wallFriction: number;
  /**
   * 0..1 — FLAT tangential loss applied on EVERY wall-contact tick, whatever
   * the angle. The anti wall-riding lever: one incidental kiss costs a sliver,
   * but LEANING on a wall (contact re-established 60×/s) compounds it far past
   * what the engine can repay — hugging the outside wall of a corner is always
   * slower than driving it (owner report, 2026-07-21: grazing walls could
   * effectively speed you up).
   */
  wallScrub: number;
  /** 0..1 — car-vs-car impulse restitution (used from Phase 2 on). */
  carBounciness: number;
  /** px — collision radius, identical for every car. NOT tunable in the panel. */
  carRadius: number;
}

export type PresetId = 'grip' | 'balanced' | 'drift';

/**
 * Authoritative bounds for every tunable physics parameter — used by the tuning
 * panel to build its sliders AND by the server to validate/clamp admin tuning
 * updates. One source of truth for both sides.
 * `carRadius` is intentionally frozen (collision geometry = fairness).
 */
export const PHYSICS_PARAM_BOUNDS: Record<keyof PhysicsConfig, { min: number; max: number }> = {
  maxSpeed: { min: 120, max: 800 },
  accel: { min: 100, max: 1400 },
  brakeDecel: { min: 200, max: 1800 },
  reverseAccel: { min: 50, max: 800 },
  reverseMaxSpeed: { min: 40, max: 300 },
  turnRate: { min: 0.5, max: 6 },
  turnInSpeed: { min: 10, max: 300 },
  highSpeedTurnDrop: { min: 0, max: 0.9 },
  lateralGrip: { min: 0.5, max: 20 },
  driftThreshold: { min: 0, max: 400 },
  driftBlendBand: { min: 10, max: 300 },
  driftGrip: { min: 0.2, max: 10 },
  rollingFriction: { min: 0, max: 3 },
  wallRestitution: { min: 0, max: 1 },
  wallFriction: { min: 0, max: 1 },
  wallScrub: { min: 0, max: 0.2 },
  carBounciness: { min: 0, max: 1 },
  carRadius: { min: 9, max: 9 },
};

/** Clamps every parameter of a config into its authorized bounds. */
export function clampPhysicsConfig(cfg: PhysicsConfig): PhysicsConfig {
  const out = { ...cfg };
  const rec = out as unknown as Record<string, number>;
  const fallback = PHYSICS_PRESETS.balanced as unknown as Record<string, number>;
  for (const [key, bounds] of Object.entries(PHYSICS_PARAM_BOUNDS)) {
    const v = rec[key];
    const usable = typeof v === 'number' && Number.isFinite(v) ? v : (fallback[key] as number);
    rec[key] = Math.min(Math.max(usable, bounds.min), bounds.max);
  }
  return out;
}

const BALANCED: PhysicsConfig = {
  maxSpeed: 420,
  accel: 520,
  brakeDecel: 800,
  reverseAccel: 320,
  reverseMaxSpeed: 150,
  turnRate: 3.0,
  turnInSpeed: 80,
  highSpeedTurnDrop: 0.25,
  lateralGrip: 8.0,
  driftThreshold: 130,
  driftBlendBand: 90,
  driftGrip: 2.6,
  rollingFriction: 0.9,
  // Auto-tamponneuse (2026-07-21): the rubber ring bounces. Walls return a
  // frank rebound and eat less tangential speed than the old scrape; car-car
  // contacts are lively. Uniform across surfaces — the rubber belongs to the
  // CAR, not the ground (every surface spreads BALANCED for these three).
  wallRestitution: 0.55,
  wallFriction: 0.35,
  // 4,5 %/tick de contact : l'appui prolongé plafonne loin sous la vitesse
  // propre (le moteur ne peut re-payer que ~accel·dt ≈ 19 px/s par tick),
  // un effleurement isolé reste imperceptible.
  wallScrub: 0.045,
  carBounciness: 0.78,
  carRadius: 9,
};

export const PHYSICS_PRESETS: Record<PresetId, PhysicsConfig> = {
  balanced: BALANCED,
  grip: {
    ...BALANCED,
    turnRate: 2.9,
    lateralGrip: 12.0,
    driftThreshold: 190,
    driftBlendBand: 80,
    driftGrip: 4.0,
  },
  drift: {
    ...BALANCED,
    turnRate: 3.3,
    highSpeedTurnDrop: 0.15,
    lateralGrip: 5.5,
    driftThreshold: 70,
    driftBlendBand: 60,
    driftGrip: 1.7,
  },
};

export const PRESET_LABELS: Record<PresetId, string> = {
  grip: 'Grip',
  balanced: 'Équilibré',
  drift: 'Glisse',
};

export function defaultPhysics(): PhysicsConfig {
  return { ...PHYSICS_PRESETS.balanced };
}

/**
 * Surfaces — a circuit mixes up to five surface types, each with its own FULL
 * physics config (owner's request, 2026-07-12). The active config is the one
 * under the car, looked up from the track sample at the car's position: purely
 * positional, identical for every car → fairness preserved, replay-safe.
 */
export type SurfaceId = 'asphalt' | 'wet' | 'dirt' | 'snow' | 'ice';

export const SURFACE_IDS: readonly SurfaceId[] = ['asphalt', 'wet', 'dirt', 'snow', 'ice'];

export const SURFACE_LABELS: Record<SurfaceId, string> = {
  asphalt: 'Asphalte',
  wet: 'Asphalte mouillé',
  dirt: 'Terre',
  snow: 'Neige/boue',
  ice: 'Glace',
};

export type SurfaceConfigs = Record<SurfaceId, PhysicsConfig>;

/**
 * Per-surface base configs — tuned for FEEL, not just for contrast (owner brief
 * 2026-07-16: asphalt ≈ Formula 1, ice ≈ extreme glide; second pass after the
 * owner's playtest: wet and dirt faster, snow = the approved Andros-style
 * drift config, ice pushed much further out; third pass 2026-07-17: wet and
 * dirt faster again — speed raised with brakes/grip lifted just enough to keep
 * corner entries manageable, drift signature preserved). Each surface plays a
 * distinct,
 * fun role; refine any of them live with the tuning panel (flip the whole test
 * track onto one surface to feel it end to end).
 *
 * Values were tuned against measured behavior (simulated maneuvers driving
 * stepCar: launch, braking, steady cornering, flick + counter-steer recovery,
 * keyboard-style ±1 inputs) and verified lap-able on the test circuit.
 * Reference numbers below: top speed / steady drift at half steer / full lock.
 * Top speeds INCLUDE the overrev (held full throttle creeps ~+4-6% past
 * maxSpeed, see stepCar). Fourth pass 2026-07-18: overrev added game-wide,
 * dirt made more sideways, and asphalt/wet/dirt brakes lifted so braking
 * distances from the new tops stay at their approved values.
 *
 * - asphalt : FORMULA 1. Huge grip, huge acceleration (0→90% in 0.65 s), huge
 *   brakes, surgical precision — it goes exactly where the nose points
 *   (666 px/s, drift 4° / 8°) and never breaks away on its own.
 * - wet     : F1 in the rain, nearly as fast as dry. Straights are attackable,
 *   but sharp inputs at speed step the tail out hard (peak 34° on a flick) and
 *   some corners demand a real lift (648 px/s, drift 7° / 37°).
 * - dirt    : the rally playground, now with WRC pace. Slides early into wide,
 *   holdable power slides — hang the rear out and steer on the throttle
 *   (fourth pass 2026-07-18: grip lowered across the board for bigger, more
 *   permanent slides — 590 px/s with overrev, drift 35° / 54°).
 * - snow    : ANDROS-STYLE DRIFT (promoted from the approved first-pass ice).
 *   Breaks into a big drift at the slightest input — permanent counter-steer —
 *   yet catchable in ~0.3 s, and the slide scrubs itself off fairly quickly
 *   (411 px/s, drift 52° / 68°).
 * - ice     : EXTREME GLIDE. Snow's drift pushed way out: the slide persists
 *   ~1.8× longer, coasting carries for ~2 500 px and braking barely works
 *   (293 px from top speed) — you manage speed BEFORE the ice, then dance on
 *   counter-steer and throttle (423 px/s, drift 66° / 77°, still self-stabilized
 *   under the ~90° steering dead zone and caught in ~0.3 s).
 *
 * Levers: lateralGrip (higher = holds), driftThreshold (higher = harder to break
 * traction), driftBlendBand (wider = smoother, more catchable slide), driftGrip
 * (grip once sliding; lower = keeps sliding), highSpeedTurnDrop (lower = rotates
 * at speed), turnInSpeed (lower = steering bites sooner), brakeDecel and
 * rollingFriction (lower = glides on).
 * CAUTION on the slippery end: keep steady full-lock drift ≤ ~80°. Beyond, the
 * forward-speed steering fade (STEER_SIGN_BAND) kills all authority and the car
 * is stuck until thrust rebuilds forward speed. driftGrip < ~0.55 also makes
 * all-or-nothing keyboard counter-steer oscillate (measured 89°↔89° pendulum at
 * 0.4-0.5) — do not go below without re-testing keyboard recovery.
 */
export function defaultSurfaceConfigs(): SurfaceConfigs {
  return {
    asphalt: {
      ...BALANCED,
      maxSpeed: 640,
      accel: 1150,
      brakeDecel: 1750,
      reverseAccel: 360,
      reverseMaxSpeed: 160,
      turnRate: 3.2,
      turnInSpeed: 65,
      highSpeedTurnDrop: 0.12,
      lateralGrip: 18.0,
      driftThreshold: 260,
      driftBlendBand: 60,
      driftGrip: 7.0,
      rollingFriction: 0.8,
    },
    wet: {
      ...BALANCED,
      maxSpeed: 625,
      accel: 1040,
      brakeDecel: 1380,
      turnRate: 3.05,
      turnInSpeed: 75,
      highSpeedTurnDrop: 0.2,
      lateralGrip: 10.2,
      driftThreshold: 75,
      driftBlendBand: 100,
      driftGrip: 3.4,
      rollingFriction: 0.9,
    },
    dirt: {
      ...BALANCED,
      maxSpeed: 575,
      accel: 840,
      brakeDecel: 960,
      turnRate: 3.35,
      turnInSpeed: 55,
      highSpeedTurnDrop: 0.1,
      lateralGrip: 5.8,
      driftThreshold: 50,
      driftBlendBand: 140,
      driftGrip: 2.2,
      rollingFriction: 1.1,
    },
    snow: {
      ...BALANCED,
      maxSpeed: 390,
      accel: 380,
      brakeDecel: 280,
      turnRate: 2.7,
      turnInSpeed: 40,
      highSpeedTurnDrop: 0.06,
      lateralGrip: 1.8,
      driftThreshold: 15,
      driftBlendBand: 90,
      driftGrip: 1.0,
      rollingFriction: 0.3,
    },
    ice: {
      ...BALANCED,
      maxSpeed: 400,
      accel: 400,
      brakeDecel: 260,
      turnRate: 2.75,
      turnInSpeed: 35,
      highSpeedTurnDrop: 0.05,
      lateralGrip: 0.9,
      driftThreshold: 10,
      driftBlendBand: 60,
      driftGrip: 0.55,
      rollingFriction: 0.15,
    },
  };
}

export function cloneCarState(s: CarState): CarState {
  return { x: s.x, y: s.y, angle: s.angle, vx: s.vx, vy: s.vy };
}

/** Advances one car by `dt` seconds. Pure and deterministic. */
export function stepCar(s: CarState, input: CarInput, cfg: PhysicsConfig, dt: number): CarState {
  const throttle = clamp01(input.throttle);
  const brake = clamp01(input.brake);
  const steer = clampSym(input.steer);

  // --- Steering ---------------------------------------------------------------
  // Authority ramps from 0 at standstill to 1 at turnInSpeed, then loses up to
  // highSpeedTurnDrop as speed approaches maxSpeed. Based on total speed so that
  // counter-steering keeps working in a slide.
  const cosA = Math.cos(s.angle);
  const sinA = Math.sin(s.angle);
  // sqrt(x²+y²) instead of Math.hypot: sqrt is the only Math function with
  // exact IEEE 754 rounding mandated by ECMA-262, so it is bit-identical across
  // JS engines — hypot is not, and this code must replay identically on client
  // and server (Phase 2 prediction/reconciliation).
  const speed = Math.sqrt(s.vx * s.vx + s.vy * s.vy);
  const forwardBefore = s.vx * cosA + s.vy * sinA;

  let authority = Math.min(speed / Math.max(cfg.turnInSpeed, 1), 1);
  if (speed > cfg.turnInSpeed && cfg.maxSpeed > cfg.turnInSpeed) {
    const over = Math.min((speed - cfg.turnInSpeed) / (cfg.maxSpeed - cfg.turnInSpeed), 1);
    authority *= 1 - cfg.highSpeedTurnDrop * over;
  }
  // Reversing flips the steering response, like a real car backing up. The flip
  // fades linearly through a small dead band around vf = 0 so that a ~90° slide
  // (forward component near zero, full authority) cannot make the response
  // oscillate between +1 and -1 from one tick to the next.
  const direction = clampSym(forwardBefore / STEER_SIGN_BAND);
  const angle = s.angle + steer * cfg.turnRate * authority * direction * dt;

  // --- Forces in the (new) car frame -------------------------------------------
  const cosN = Math.cos(angle);
  const sinN = Math.sin(angle);
  // forward = (cosN, sinN), right = (-sinN, cosN)
  let vf = s.vx * cosN + s.vy * sinN;
  let vl = -s.vx * sinN + s.vy * cosN;

  // Thrust and reverse thrust push *toward* their speed cap but never clamp an
  // existing overspeed: a state arriving beyond the cap (car-vs-car impulse in
  // Phase 2, wall bounce, tuned-down caps) decays smoothly through friction
  // instead of losing tens of px/s in a single 16.7 ms tick.
  // The gate on TOTAL speed matters: refilling vf to the cap every tick while
  // the frame rotates would otherwise pump energy into a steady power-slide
  // 10-20% faster than the straight-line top (measured on wet) — you must never
  // corner faster than the engine can go straight.
  const overrevCeiling = cfg.maxSpeed * (1 + OVERREV_HEADROOM);
  if (throttle > 0 && speed < overrevCeiling) {
    if (vf < cfg.maxSpeed) {
      vf = Math.min(vf + cfg.accel * throttle * dt, cfg.maxSpeed);
    }
    // Overrev: once pinned at the limiter, the engine keeps straining past it,
    // closing exponentially (OVERREV_RATE) on a ceiling +OVERREV_HEADROOM above
    // maxSpeed. The push must re-pay this tick's rolling-friction loss scaled by
    // throttle, so only (near-)full throttle sustains the overspeed — lift and
    // the speed bleeds back under the cap on its own. It also needs traction:
    // the push fades with lateral slip (OVERREV_SLIP_FADE) — a straight-line
    // mechanic, otherwise it silently raises steady cornering speeds on
    // low-grip surfaces far beyond what their corners were tuned for. States
    // already beyond the ceiling (external impulses) are left to friction.
    if (vf >= cfg.maxSpeed && vf < overrevCeiling) {
      const cleanRun = Math.max(0, 1 - Math.abs(vl) / OVERREV_SLIP_FADE);
      if (cleanRun > 0) {
        const frictionLoss = vf * (1 - Math.exp(-cfg.rollingFriction * dt));
        const strain = (overrevCeiling - vf) * (1 - Math.exp(-OVERREV_RATE * dt));
        vf = Math.min(vf + (frictionLoss + strain) * throttle * cleanRun, overrevCeiling);
      }
    }
  }
  if (brake > 0) {
    if (vf > 1e-3) {
      // Braking never pushes the car backward within a tick.
      vf = Math.max(vf - cfg.brakeDecel * brake * dt, 0);
    } else if (vf > -cfg.reverseMaxSpeed) {
      vf = Math.max(vf - cfg.reverseAccel * brake * dt, -cfg.reverseMaxSpeed);
    }
  }

  // Rolling friction: exponential decay is unconditionally stable for any dt.
  vf *= Math.exp(-cfg.rollingFriction * dt);

  // Lateral grip: full absorption below the drift threshold, blending down to
  // driftGrip across the blend band — small drifts are controllable, never brutal.
  const absVl = Math.abs(vl);
  let grip = cfg.lateralGrip;
  if (absVl > cfg.driftThreshold) {
    const t = Math.min((absVl - cfg.driftThreshold) / Math.max(cfg.driftBlendBand, 1), 1);
    grip = cfg.lateralGrip + (cfg.driftGrip - cfg.lateralGrip) * t;
  }
  vl *= Math.exp(-grip * dt);

  // --- Recompose and integrate --------------------------------------------------
  const vx = cosN * vf - sinN * vl;
  const vy = sinN * vf + cosN * vl;

  return {
    x: s.x + vx * dt,
    y: s.y + vy * dt,
    angle: wrapAngle(angle),
    vx,
    vy,
  };
}

/** px/s — width of the dead band over which the reverse-steering flip fades. */
const STEER_SIGN_BAND = 15;

/** 0..1 — how far past maxSpeed a held full throttle can overrev, as a fraction. */
export const OVERREV_HEADROOM = 0.06;
/** 1/s — exponential rate at which the overrev closes on its ceiling. */
export const OVERREV_RATE = 0.6;
/** px/s — lateral speed at which the overrev push has fully faded out. */
export const OVERREV_SLIP_FADE = 40;

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function clampSym(v: number): number {
  return v < -1 ? -1 : v > 1 ? 1 : v;
}
